import { SearchIcon } from 'foundations-assets/icons/components/SearchIcon';
import { Flex } from 'foundations-components/components/Flex';
import { Text } from 'foundations-components/components/Text';
import { IconHolder } from 'foundations-components/transitional/components/IconHolder';
import { Tag } from 'foundations-components/transitional/components/Tag';
import { Tooltip } from 'foundations-components/transitional/components/Tooltip';
import FormattedMessage from 'I18n/components/FormattedMessage';
import unescapedText from 'I18n/utils/unescapedText';
import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { EcosystemSlider } from 'trellis-ecosystem';
import RouterLink from 'ui-addon-react-router-dom/RouterLink';
import { SEARCH_QUERY as PUBLIC_SEARCH_QUERY } from '../../__generated__/chirp/com/hubspot/marketplace/personalization/rpc/PersonalizationPublicRpc.dfc.new';
import { SEARCH_QUERY } from '../../__generated__/chirp/com/hubspot/marketplace/personalization/rpc/PersonalizationRpc.dfc.new';
import { Paths } from '../constants/paths';
import { DEFAULT_MAX_SECTION_CARD_COUNT } from '../constants/suggestions';
import { findOptionText, getFlattenedOptions } from '../filters/filterUtils';
import { useFilterConfig } from '../filters/hooks/useFilterConfig';
import { useSupportedListingOptions } from '../filters/hooks/useSupportedListingOptions';
import { usePersonalizationQuery } from '../hooks/usePersonalizationQuery';
import { useProductTypes } from '../hooks/useProductTypeSearchParams';
import { DATA_TYPE_MAP, parseAppliedFilterValues } from '../search-results/utils/searchMappers';
import { getPartnerTierContent } from '../utils/getPartnerTierContent';
import { StorefrontListingCard } from './cards/StorefrontListingCard';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
function useContinueBrowsingLocalStorageKey() {
  const productTypes = useProductTypes();
  if (productTypes.length !== 1) return '';
  return `eco_CONTINUE_BROWSING_${productTypes[0]}`;
}
export function useContinueBrowsingRecorder() {
  const key = useContinueBrowsingLocalStorageKey();
  const location = useLocation();
  useEffect(() => {
    try {
      if (key) localStorage.setItem(key, location.search);
    } catch (e) {
      // ignore
    }
  }, [key, location.search]);
}
export function ContinueBrowsingSection() {
  var _params$getAll, _params$get, _searchResult$data$re, _searchResult$data;
  const productTypes = useProductTypes();
  const filters = useFilterConfig();
  const storageKey = useContinueBrowsingLocalStorageKey();
  let storedSearch = '';
  try {
    var _localStorage$getItem;
    storedSearch = (_localStorage$getItem = localStorage.getItem(storageKey)) !== null && _localStorage$getItem !== void 0 ? _localStorage$getItem : '';
  } catch (e) {
    // ignore
  }
  const params = storedSearch ? new URLSearchParams(storedSearch) : undefined;
  const supportedListingIds = (_params$getAll = params === null || params === void 0 ? void 0 : params.getAll('eco_PROFILE_SUPPORTED_LISTINGS')) !== null && _params$getAll !== void 0 ? _params$getAll : [];
  const supportedListingOptions = useSupportedListingOptions(supportedListingIds);
  const filtersByField = {};
  filtersByField.PRODUCT_TYPE = [{
    values: productTypes,
    clause: 'OR',
    negation: false,
    __typename: DATA_TYPE_MAP.STRING
  }];
  new Set(params === null || params === void 0 ? void 0 : params.keys()).forEach(paramKey => {
    const trimmedKey = paramKey.replace('eco_', '');
    const filter = filters[trimmedKey];
    if (!filter) return;
    filtersByField[trimmedKey] = [{
      values: parseAppliedFilterValues({
        dataType: filter.dataType,
        values: params.getAll(paramKey)
      }),
      clause: 'OR',
      negation: false,
      __typename: DATA_TYPE_MAP[filter.dataType]
    }];
  });
  const searchResult = usePersonalizationQuery({
    app: SEARCH_QUERY,
    public: PUBLIC_SEARCH_QUERY,
    options: {
      skip: !params || !!filters.error || filters.loading,
      variables: {
        filter: {
          filterGroups: [{
            filtersByField,
            clause: 'AND',
            negation: false
          }],
          clause: 'AND',
          negation: false
        },
        length: DEFAULT_MAX_SECTION_CARD_COUNT,
        search: (_params$get = params === null || params === void 0 ? void 0 : params.get('eco_search')) !== null && _params$get !== void 0 ? _params$get : undefined
      }
    }
  });
  const cards = (_searchResult$data$re = (_searchResult$data = searchResult.data) === null || _searchResult$data === void 0 ? void 0 : _searchResult$data.result.cards) !== null && _searchResult$data$re !== void 0 ? _searchResult$data$re : [];
  if (!params || filters.error || filters.loading || !cards.length) return null;
  return /*#__PURE__*/_jsxs(Flex, {
    flexDirection: "column",
    marginBlockStart: "400",
    marginBlockEnd: "800",
    children: [/*#__PURE__*/_jsx(Text, {
      as: "h3",
      typography: "heading-400",
      marginBlockEnd: "400",
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "marketplaceStorefront.sliderSection.continueBrowsing"
      })
    }), /*#__PURE__*/_jsxs(Flex, {
      gap: "200",
      children: [params.entries().toArray().map(([paramKey, paramValue]) => {
        const reactKey = `${paramKey}-${paramValue}`;
        if (paramKey === 'eco_search') {
          return /*#__PURE__*/_jsx(ContinueBrowsingTag, {
            text: paramValue,
            icon: SearchIcon
          }, reactKey);
        }
        const filter = filters[paramKey.replace('eco_', '')];
        if (!filter) return null;
        if (filter.field === 'PROFILE_SUPPORTED_LISTINGS') {
          const text = findOptionText(supportedListingOptions, paramValue);
          return /*#__PURE__*/_jsx(ContinueBrowsingTag, {
            text: text,
            tooltip: filter.label
          }, reactKey);
        }
        if (filter.field === 'APP_LANGUAGES' || filter.field === 'PROFILE_LANGUAGES') {
          return /*#__PURE__*/_jsx(ContinueBrowsingTag, {
            text: unescapedText(`SharedI18nStrings.languageNames.${paramValue}`),
            tooltip: filter.label
          }, reactKey);
        }
        if (filter.field === 'PROFILE_SOLUTIONS_PARTNER_TIER') {
          const tierContent = getPartnerTierContent(paramValue);
          if (!tierContent) return null;
          return /*#__PURE__*/_jsx(ContinueBrowsingTag, {
            tooltip: filter.label,
            icon: tierContent.icon,
            text: tierContent.text
          }, reactKey);
        }
        const options = getFlattenedOptions(filter);
        const text = findOptionText(options, paramValue);
        return /*#__PURE__*/_jsx(ContinueBrowsingTag, {
          text: text,
          tooltip: filter.label
        }, reactKey);
      }), /*#__PURE__*/_jsx(RouterLink, {
        to: `${Paths.SEARCH_RESULTS_SOLUTIONS_PARTNERS}?${params}`,
        marginInlineStart: "auto",
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "marketplaceStorefront.sliderSection.seeMore"
        })
      })]
    }), /*#__PURE__*/_jsx(EcosystemSlider, {
      cardWidth: 306,
      children: cards.map((card, index) => /*#__PURE__*/_jsx(StorefrontListingCard, {
        card: card,
        resultCount: 0,
        resultIndex: index
      }, card.listingId))
    })]
  });
}
function ContinueBrowsingTag({
  text,
  tooltip,
  icon
}) {
  if (!text) return null;
  return /*#__PURE__*/_jsx(Tooltip, {
    title: tooltip,
    children: /*#__PURE__*/_jsxs(Tag, {
      margin: "0",
      children: [icon && /*#__PURE__*/_jsx(IconHolder, {
        icon: icon,
        compact: true,
        marginInlineEnd: "100",
        verticalAlign: "-0.13em"
      }), text]
    })
  });
}